import React, { FunctionComponent, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import styled, { keyframes } from "styled-components";
import { NodeActionActions } from "../../state/nodeActionState";
import { nodeClickDataActions } from "../../state/nodeClickDataState";
import { featureRecommendationActions } from "../../state/featureRecommendationState";
import { useAppSelector } from "../../state/store";
import { IconMapping, NodeConfig } from "../../utilities/flowBuilder.utility";
import FooterComponent from "../footer-component/footerComponent";
import HeaderComponent from "../header-component/headerComponent";
import { useOutside } from "../rich-text-editor/useOutside";
import ComponentModalHook from "./componentModal.hook";
import { Tag } from "@bikdotai/bik-component-library";
import {
  getDoNotRemindMeForEscapeToCloseFromLocalStorage,
  setDoNotRemindMeForEscapeToCloseFromLocalStorage,
} from "../../utilities/localStorageUtility";
import { EscapeClickConfirmationModal } from "../escape-confirmation-modal";
import { ConfirmationModal } from "../confirmation-modal";
import HelpSection from "../help-section";
import { getMacroChannelLabel } from "../../constants/MacroChannels";
import { NodeSubType } from "../../models/nodeSubType";
import { useVersionJet } from "../../utilities/useVersionJet";
import { CHANNELS } from "../../utilities/login.utility";
import { DEPRECATED_AI_BLOCK_IDS } from "../modal-configs/whatsappMessageBlockConfigs";
import DeprecatedBlockBanner from "../deprecated-block-banner/deprecatedBlockBanner";

interface ComponentModalProps {
  id?: string;
  storeId: string;
  flowId: string;
  config: NodeConfig;
  closeModal: () => void;
}

export enum ComponentModalType {
  google_sheet_add_row = "google_sheet_add_row",
  google_sheet_update_row = "google_sheet_update_row",
  google_sheet_get_row_data = "google_sheet_get_row_data",
  email_temp_new = "email_temp_new",
  filter = "filter",
  carousel = "carousel",
  browse_collection = "browse_collection",
  send_sms = "send_sms",
  send_rcs = "send_rcs",
  wa_carousel = "wa_carousel",
  email_csat = "email_csat",
}

const READ_ONLY_BLOCKS = ["evaluate", "store_variable", "call_api"];
const AI_READ_ONLY_BLOCKS = ["ai_general_purpose_assistant"];
const NOT_IN_COMPONENT_MODAL_BLOCKS: string[] = [
  "filter",
  "discount_code",
  NodeSubType.ORDER_FILTER,
];

export const ComponentModalContext = React.createContext<{
  setHasUnsavedChanges: (val: boolean) => void;
  setDisableSave: (val: boolean) => void;
  isMacro: boolean;
} | null>(null);

type Props = ComponentModalProps;

const generateTitle = (type: string, channel?: string): string => {
  switch (type) {
    case "add_ticket_label":
      return "Add Label";
    case "ab_test":
      return "AB Testing";
    case "call_custom_api":
      return "Other Actions";
    case "broadcast_events":
      return "Opt In/Out";
    case "ai_classify":
      return "AI Classify";
    case "ai_product_recommend":
      return "AI Product Recommend";
    case "ai_reply":
      return "AI Reply";
    case "send_rcs": 
      return "RCS";
    case NodeSubType.AI_GENERAL_PURPOSE_ASSISTANT:
      return "AI Assistant";
    case NodeSubType.ORDER_TRACKING_ASSISTANT:
      return "AI Order Tracking Agent";
    case NodeSubType.AI_AGENT:
      return "AI Agent";
    case NodeSubType.AGENTIC_BUILDER:
      return "AI Agent";
    case NodeSubType.AI_ASK:
      return "AI Ask";
    case NodeSubType.AI_VOICE_AGENT:
      return "AI Voice Agent";
    case "email_temp_new":
      return "Email";
    case "wa_carousel":
      return "Carousel";
    case "email_csat":
      return "Email CSAT/NPS";
    case NodeSubType.MULTI_ACTION:
      return "Ticket actions";
    case NodeSubType.CONNECT_TO_FLOW:
      return "Connect to flow";
    case NodeSubType.GMAIL_REPLY:
      return "Email Reply";
    case NodeSubType.WHATSAPP_PAYMENT:
      return "Whatsapp Payment";
    case NodeSubType.ORDER_CONFIRMATION:
      return "Order Confirmation";
    case NodeSubType.WHATSAPP_ADDRESS:
      return "Ask Address";
    case NodeSubType.RESPONSE_BLOCK:
      return channel ? getMacroChannelLabel(channel) + " response" : "Message";
    case NodeSubType.MACRO_STARTING_POINT:
      return "Starting point";
    case NodeSubType.STICKY_NOTES:
      return "Sticky notes";
    case NodeSubType.TICKET_FORM:
      return "Ticket Form";
    case NodeSubType.INSTAGRAM_TEXT:
      if (channel === CHANNELS.LIVECHAT) {
        return "Livechat Text";
      } else {
        return "Instagram Text";
      }
    default:
      return type.split("_").join(" ");
  }
};

const ComponentModal: FunctionComponent<Props> = (props) => {
  const { config, id, storeId, flowId } = props;
  const [saveTo, setSaveTo] = useState<string>("");
  const nodeClickState = useAppSelector((state) => state.nodeClickState);
  const featureRecommendation = useAppSelector(
    (state) => state.featureRecommendationState.feature
  );
  const nodeFlow = useAppSelector((state) => state.storeState.flow);
  const [nodeData, setNodeData] = useState<any>({});
  const homeState = useAppSelector((state) => state.homeState);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const { lazyLoadComponent } = ComponentModalHook(storeId, flowId);
  const dispatcher = useDispatch();
  const nodeActions = useAppSelector((state) => state.nodeActionState);
  const componentModal = useRef<any>();
  const contentRef = useRef<any>();
  const carouselContentRef = useRef<any>();
  const keyboardEventDataState = useAppSelector(
    (state) => state.keyboardEventState
  );
  const [isEscapePressed, setIsEscapePressed] = useState(false);
  const [isDontRemindMeAgainSelected, setIsDontRemindMeAgainSelected] =
    useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const escapeToCloseFromLocalStorage =
    getDoNotRemindMeForEscapeToCloseFromLocalStorage(storeId, flowId);
  const { isVersionApplicabletoPlan } = useVersionJet();

  const isSSO = homeState.userData?.loginType === "sso";
  const isReadOnly =
    (!isSSO &&
      READ_ONLY_BLOCKS.includes(nodeData?.subtype) &&
      !isVersionApplicabletoPlan(nodeData?.subType)) ||
    AI_READ_ONLY_BLOCKS.includes(nodeData?.subType);

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean | null>(
    null
  );
  const [disableSave, setDisableSave] = useState(false);

  useOutside(
    componentModal,
    (target) => {
      // If exactly false, the active block has declared it is clean, close silently.
      if (hasUnsavedChanges === false) {
        onClickCancel();
      } else {
        // Otherwise, fall back to safe legacy discard check
        onFocusOut();
      }
    },
    [contentRef, carouselContentRef]
  );

  const onFocusOut = () => {
    const element = document.getElementById("cmpModal");
    if (element && element.clientWidth === 0 && element.clientHeight === 0) {
      return;
    }
    let list = element?.classList;
    list?.add("focus");
    setShowConfirmationModal(true);
    setTimeout(() => {
      list?.remove("focus");
    }, 4000);
  };

  useEffect(() => {
    setIsModalOpen(true);
  }, []);

  useEffect(() => {
    if (!nodeClickState.nodeId || !nodeClickState.nodeSubType) return;
    // A drawer that disabled Save must not leave it disabled for the next node.
    setDisableSave(false);
    const selectedNodeId = nodeClickState.nodeId as string;
    const nodeType = nodeClickState.nodeType;
    const nodeSubType = nodeClickState.nodeSubType || "";
    // For macro nodes, read conf from nodeClickState.backendConfig (React Flow node = single source of truth)
    const nodeData = nodeClickState.backendConfig
      ? nodeClickState.backendConfig
      : nodeType === "Sticky notes"
        ? config
        : nodeFlow[selectedNodeId] || {};
    setNodeData({
      conf: nodeData,
      subtype: nodeSubType,
      nodeId: selectedNodeId,
    });
  }, [nodeClickState?.nodeId, nodeClickState?.nodeSubType]);

  useEffect(() => {
    if (saveTo && saveTo === "close") {
      closeModal();
    }
  }, [saveTo]);

  useEffect(() => {
    if (nodeActions?.action === "delete" && nodeActions?.nodeId) {
      onClickCancel();
      setSaveTo("close");
      dispatcher(
        NodeActionActions.updateState({
          action: "",
          nodeId: "",
          type: "",
        })
      );
    }
  }, [nodeActions]);

  const onClickSave = () => {
    setSaveTo(nodeData?.subtype);
    setShowConfirmationModal(false);
  };

  const onLoading = (value: boolean) => {
    setLoading(value);
  };

  const onClickCancel = () => {
    dispatcher(
      nodeClickDataActions.setEditNode({
        nodeId: "",
        nodeType: "",
        nodeSubType: "",
        data: {},
      })
    );
    // Clear feature recommendation when modal closes
    dispatcher(featureRecommendationActions.clearFeature());
    closeModal();
    setShowConfirmationModal(false);
    setSaveTo("cancel");
  };

  const closeModal = () => {
    setIsModalOpen(false);
    // after the close animation is completed
    setTimeout(() => {
      props.closeModal();
    }, 500);
  };

  /**
   * Runs when any action is dispatched when on keyboardEventDataState.
   * Basically, anytime a keyboard event which is used in the code is captured, the action to be dispatched.
   */
  useEffect(() => {
    //When user presses escape key | Depending on local storage value, show the popup or close the modal
    if (keyboardEventDataState.keyPressAction === "escape") {
      getDoNotRemindMeForEscapeToCloseFromLocalStorage(storeId, flowId)
        ? onClickCancel()
        : setIsEscapePressed(true);
    }
  }, [keyboardEventDataState?.keyPressAction]);

  /**
   * When user presses "Yes" on popup.
   * Stores value in local storage if user checks 'Don't remind me again'
   */
  const onYesFromEscapeConfirmModal = () => {
    if (isDontRemindMeAgainSelected) {
      setDoNotRemindMeForEscapeToCloseFromLocalStorage(storeId, flowId, "true");
    }
    onClickCancel();
    setIsEscapePressed(false);
  };
  const isMacroActive = !!homeState?.isMacro;

  return (
    <ComponentModalContext.Provider
      value={{ setHasUnsavedChanges, setDisableSave, isMacro: isMacroActive }}
    >
      <ComponentModalStyles subType={nodeData.subtype} isMacro={isMacroActive}>
        <div
          className={`component__modal ${isModalOpen ? "open" : "close"}`}
          ref={componentModal}
          id="cmpModal"
        >
          {!NOT_IN_COMPONENT_MODAL_BLOCKS.includes(nodeData?.subtype) && (
            <div className="header">
              <HeaderComponent
                isMacro={isMacroActive}
                channel={homeState.channel || "whatsapp"}
                type={nodeClickState.nodeType as string}
                headerType={generateTitle(
                  nodeData?.subtype || "",
                  nodeData?.subtype === NodeSubType.RESPONSE_BLOCK
                    ? nodeData?.conf?.channel ||
                        nodeClickState.automationChannel
                    : homeState.channel!
                )}
                headerIcon={IconMapping(
                  nodeData?.subtype,
                  nodeData?.subtype === NodeSubType.RESPONSE_BLOCK
                    ? nodeData?.conf?.channel ||
                        nodeClickState.automationChannel
                    : homeState.channel!
                )}
                subType={(nodeClickState.nodeSubType as string) || ""}
              />
            </div>
          )}
          {isReadOnly && (
            <div style={{ margin: 10, marginBottom: 0 }}>
              <Tag
                tagText={
                  "🔒 Read-Only Mode: you can only view the content but cannot make any modifications."
                }
                type="warning"
              />
            </div>
          )}

          {nodeData.subtype === "carousel" ? (
            <div className="carousalContent" ref={carouselContentRef}>
              {lazyLoadComponent(
                nodeData,
                saveTo,
                setSaveTo,
                config,
                error,
                setError,
                setLoading,
              featureRecommendation
              )}
              <HelpSection subType={nodeData?.subtype} />
            </div>
          ) : (
            <div
              className="content"
              id="content-body"
              ref={contentRef}
              style={{ pointerEvents: isReadOnly ? "none" : "unset" }}
            >
              {lazyLoadComponent(
                nodeData,
                saveTo,
                setSaveTo,
                config,
                error,
                setError,
                setLoading,
              featureRecommendation
              )}
              <HelpSection subType={nodeData?.subtype} />
            </div>
          )}

          <div className="footer">
            {DEPRECATED_AI_BLOCK_IDS.has(nodeData?.subtype) && (
              <DeprecatedBlockBanner />
            )}
            <FooterComponent
              loading={loading}
              onClickCancel={onClickCancel}
              onClickSave={onClickSave}
              error={error}
              isReadOnly={isReadOnly}
              isMacro={isMacroActive}
              hasUnsavedChanges={hasUnsavedChanges}
              disableSave={disableSave}
            />
          </div>
        </div>
        <ConfirmationModal
          loading={loading}
          open={showConfirmationModal}
          onPositiveClick={onClickSave}
          onNegativeClick={onClickCancel}
          onCloseClick={() => setShowConfirmationModal(false)}
          title={"Save changes to block?"}
          subTitle={
            "You have unsaved changes in this block. Do you wish to save the changes?"
          }
          positiveButtonText={"Save"}
          negativeButtonText={"Discard"}
        />
        <div className="freeze__screen"></div>
        {isEscapePressed && !escapeToCloseFromLocalStorage && (
          <EscapeClickConfirmationModal
            onPositiveButtonClick={onYesFromEscapeConfirmModal}
            onNegativeButtonClick={() => setIsEscapePressed(false)}
            isChecked={isDontRemindMeAgainSelected}
            onCheckedChange={() =>
              setIsDontRemindMeAgainSelected(!isDontRemindMeAgainSelected)
            }
          />
        )}
      </ComponentModalStyles>
    </ComponentModalContext.Provider>
  );
};

const slideIn = keyframes`
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(0);
  }
`;

const slideOut = keyframes`
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-100%);
  }
`;

const ComponentModalStyles = styled.div.attrs(
  (props: { subType?: string; isMacro?: boolean }) => props
)`
  ::-webkit-scrollbar {
    width: 0px;
    background: transparent;
  }
  .component__modal {
    position: absolute;
    height: 100%;

    width: 424px;
    top: -56px;
    left: 0;
    z-index: 11;
    display: flex;
    flex-direction: column;
    background: ${(props: any) => (props.isMacro ? "#ffffff" : "#fafafa")};

    margin-top: 56px;
    box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.12);
    &.open {
      animation: ${slideIn} 0.5s forwards;
    }
    &.close {
      animation: ${slideOut} 0.5s forwards;
    }
  }
  .focus {
    transition-delay: 0.2s;
    outline: 2px solid #731dcf;
    box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.12);
  }
  .freeze__screen {
    position: absolute;
    top: -56px;
    left: 0;
    margin-left: 424px;
    margin-top: 56px;
    width: 10000px;
    height: 100%;
    opacity: 0;
    z-index: 10;
  }

  .header {
    position: sticky;
    background: ${(props: any) => (props.isMacro ? "#ffffff" : "#fafafa")};
    top: 0;
    z-index: 10;
  }

  .footer {
    position: sticky;
    bottom: 0;
    margin-top: auto;
    z-index: 98;
  }

  .content {
    overflow-y: auto;
    padding: 24px 16px 24px 16px;
    flex: 1;
    z-index: 99;
    overflow-x: hidden;
    ::-webkit-scrollbar {
      width: 0px;
      background: transparent;
    }
  }

  .carousalContent {
    display: flex;
    flex-direction: column;
    padding: ${(props) =>
      props.subType === "carousel" ? "16px 16px 0" : "60px"};
    flex: 1;
    z-index: 99;
    overflow-y: auto;
  }
`;

export default ComponentModal;
